import type { AdminMe } from '@trendator/shared';
import { useCallback, useEffect, useState } from 'react';
import { Layout } from './components/Layout';
import { ErrorBox, Loading } from './components/ui';
import { ApiError, api, setUnauthorizedHandler } from './lib/api';
import { match, navigate, useLocation } from './lib/router';
import { Analytics } from './pages/Analytics';
import { Audit } from './pages/Audit';
import { Dashboard } from './pages/Dashboard';
import { Generations } from './pages/Generations';
import { Login } from './pages/Login';
import { Moderation } from './pages/Moderation';
import { Partners } from './pages/Partners';
import { Payments } from './pages/Payments';
import { Providers } from './pages/Providers';
import { Queues } from './pages/Queues';
import { Trends } from './pages/Trends';
import { UserCard } from './pages/UserCard';
import { Users } from './pages/Users';

type Auth = { kind: 'loading' } | { kind: 'out' } | { kind: 'error'; text: string } | { kind: 'in'; me: AdminMe };

export function App() {
  const [auth, setAuth] = useState<Auth>({ kind: 'loading' });
  const { path, query } = useLocation();

  const check = useCallback(async () => {
    try {
      setAuth({ kind: 'in', me: await api.get<AdminMe>('/me') });
    } catch (err) {
      setAuth(err instanceof ApiError && err.status === 401 ? { kind: 'out' } : { kind: 'error', text: err instanceof Error ? err.message : 'Нет связи' });
    }
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(() => setAuth({ kind: 'out' }));
    void check();
  }, [check]);

  const onLogin = useCallback((me: AdminMe) => setAuth({ kind: 'in', me }), []);

  if (auth.kind === 'loading') return <Loading />;
  if (auth.kind === 'error') {
    return (
      <div className="mx-auto max-w-md p-6">
        <ErrorBox text={auth.text} onRetry={() => void check()} />
      </div>
    );
  }
  if (auth.kind === 'out') return <Login onLogin={onLogin} />;

  const logout = async () => {
    await api.post('/auth/logout').catch(() => {});
    setAuth({ kind: 'out' });
    navigate('/');
  };

  let page;
  let m;
  if (path === '/') page = <Dashboard me={auth.me} />;
  else if (path === '/trends') page = <Trends id={null} />;
  else if ((m = match('/trends/:id', path))) page = <Trends key={m.id} id={m.id!} />;
  else if (path === '/generations') page = <Generations query={query} />;
  else if (path === '/users') page = <Users query={query} />;
  else if ((m = match('/users/:id', path))) page = <UserCard key={m.id} id={m.id!} />;
  else if (path === '/payments') page = <Payments query={query} />;
  else if (path === '/partners') page = <Partners query={query} />;
  else if (path === '/providers') page = <Providers />;
  else if (path === '/queues') page = <Queues />;
  else if (path === '/moderation') page = <Moderation query={query} />;
  else if (path === '/analytics') page = <Analytics query={query} />;
  else if (path === '/audit') page = <Audit />;
  else page = <ErrorBox text="Такой страницы нет" onRetry={() => navigate('/')} />;

  return (
    <Layout path={path} me={auth.me} onLogout={() => void logout()}>
      {page}
    </Layout>
  );
}
